import lenderSubmitApplication from 'signals/SubmitApplication.signal';
import submitApplicationSignal, {
  applicationDraftSignal,
} from 'signals/SubmitApplication.signal';
import { Alert, Button, Card, Container, Row } from 'react-bootstrap';
import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SubmitApplicationsChooseProgram from './components/SubmitApplicationsChooseProgram';
import SubmitApplicationsOCR from './components/SubmitApplicationsOCR';
import SubmitApplicationsSuccessPage from './components/SubmitApplicationsSuccessPage';
import SubmitApplicationAdditionalDocuments from './components/SubmitApplicationAdditionalDocuments';
import {
  fetchAndSetApplicationDrafts,
  fetchAndSetFundingPrograms,
  handleClearLock,
  handleNavigateToOcrFromApplicationDetailView,
  handleSaveApplicationDraft,
  saveApplicationDraft,
  reset, setLockNotificationListener,
  tearDownLockNotificationListener
} from './SubmitApplications.helpers';
import SubmitApplicationsNote from './components/SubmitApplicationsNote';
import BackButton from '../BackButton';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { warningIcon } from '../Constant/Icons';
import ChooseApplicationDraftModal from './components/ChooseApplicationDraftModal';
import RemoveAccessModal from './components/RemoveAccessModal';
import Loader from 'components/global/Loader';

const SubmitApplications = () => {
  const { draftPendingRequest } = applicationDraftSignal.value;
  const { step } = submitApplicationSignal.value;
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const location = useLocation();
  const portal = location.pathname.split('/')[1];
  const init = useCallback(async () => {
    await fetchAndSetFundingPrograms();
    await fetchAndSetApplicationDrafts();
    await setLockNotificationListener();
  });

  useEffect(() => {
    init();
  }, []);

  // eslint-disable-next-line
  useEffect(() => {
    applicationDraftSignal.update({ selectedDraft: {} });
    reset();

    if (draftPendingRequest.id) {
      setIsDraftLoading(true);
      handleNavigateToOcrFromApplicationDetailView()
        .finally(() => setIsDraftLoading(false));
    }

    return () => {
      if (applicationDraftSignal.value.selectedDraft?.id) {
        handleClearLock();
      }
      applicationDraftSignal.update({ draftPendingRequest: {} });
      tearDownLockNotificationListener();
    };
  }, []);

  useEffect(() => {
    let autosave = undefined;

    if (step > 1 && step < 5) {
      autosave = setInterval(() => {
        saveApplicationDraft();
      }, 150000);
    }

    return () => clearInterval(autosave);
  }, [step]);

  const showStep = () => {
    switch (step) {
      case 1:
        return <SubmitApplicationsChooseProgram />;
      case 2:
        return <SubmitApplicationsOCR />;
      case 3:
        return <SubmitApplicationAdditionalDocuments />;
      case 4:
        return <SubmitApplicationsNote />;
      case 5:
        return <SubmitApplicationsSuccessPage />;
      default:
        lenderSubmitApplication.reset();
        return null;
    }
  };
  return (
    <Container className="p-16 bg-light">
      <h2 className="text-center">Submit Application</h2>
      <Row className="px-16 justify-content-between mb-16">
        <BackButton
          to={`/${portal}/portfolio`}
          buttonText="Back to Program Requests"
        />
        <Alert
          className="p-8 mb-0"
          hidden={!applicationDraftSignal.value.selectedDraft?.id}
        >
          <p className="mb-0">
            <b>In Progress:</b>{' '}
            {submitApplicationSignal.value.fundingProgram?.name}
            {applicationDraftSignal.value.selectedDraft?.payload?.businessEin &&
              ` for business ${applicationDraftSignal.value.selectedDraft?.payload?.businessEin}`}
          </p>
        </Alert>
        <div>
          <Button
            variant="outline-primary mr-16"
            className="rounded"
            onClick={() => handleSaveApplicationDraft()}
          >
            Save Progress
          </Button>
          <Button
            className="rounded mr-16"
            variant="outline-primary"
            onClick={() =>
              applicationDraftSignal.update({ showDraftModal: true })
            }
          >
            Manage Drafts
          </Button>
          <Button
            variant="outline-secondary"
            className="rounded"
            onClick={reset}
          >
            Reset Progress
          </Button>
        </div>
      </Row>
      <Card
        className="px-24 py-32 rounded-0"
        hidden={
          portal !== 'lender' ||
          submitApplicationSignal.value.fundingPrograms.length
        }
      >
        <Alert variant="warning">
          <FontAwesomeIcon icon={warningIcon} size="lg" />
          <br />
          You must be approved for a program before adding an application. You
          can submit program requests and check the status of active requests{' '}
          <Link to="/lender/program-requests">here</Link>.
        </Alert>
      </Card>
      {isDraftLoading &&
        step === 1 &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <div
              style={{
                padding: '32px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Loader message="Loading draft..." />
            </div>
          </div>,
          document.body
        )}
      <Card
        className="px-24 py-32 rounded-0 position-relative overflow-hidden"
        hidden={
          portal === 'lender' &&
          !submitApplicationSignal.value.fundingPrograms.length
        }
      >
        {showStep()}
        <small className="text-right">
          * A draft will be saved when changing pages
        </small>
      </Card>
      <ChooseApplicationDraftModal />
      <RemoveAccessModal />
    </Container>
  );
};

export default SubmitApplications;
